import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Flex, FlexColumn } from 'core/components';
import Page, { NAV_HEIGHT } from 'app/components/page/Page';
import NotFound from 'app/views/NotFound';
import BoardGridView from 'app/views/boards/BoardGridView';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import BoardTitle from 'app/views/boards/BoardTitle';
import { HotkeyGroup } from 'app/util/constants/hotkeys';
import { HotkeysTarget2 } from '@blueprintjs/core';
import BoardKioskHeader from 'app/views/boards/BoardKioskHeader';
import UdeToolBar from 'app/views/boards/panels/ude/UdeToolBar';
import LabelList from 'app/components/labels/LabelList';
import { useBoardScrollBehavior } from 'app/views/boards/useBoardScrollBehavior';
import BoardCollapsibleHeader from 'app/views/boards/BoardCollapsibleHeader';
import BoardFiltersDrawer from 'app/views/boards/BoardFiltersDrawer';
import BoardSettings from './BoardSettings';
import BoardSubnavTools from './BoardSubnavTools';
import UnsavedChangesPrompt from './UnsavedChangesPrompt';

const BreadcrumbTitleInput = styled.input`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  color: inherit;
  font-size: inherit;
  font-weight: normal;
  font-family: inherit;
  line-height: inherit;
  padding: 4px;
  outline: none;
  max-width: 300px;
  width: auto;
  text-overflow: ellipsis;
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BoardContentColumn = styled(FlexColumn)`
  transition: height 200ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

@withRouter
@inject('$app', '$boards')
@observer
class BoardView extends Component {
  state = {
    isSettingsOpen: false,
    isFiltersOpen: false
  };

  constructor(props) {
    super(props);
    this.state.isSettingsOpen = props.location.state?.isEditing ?? false;
  }

  componentWillUnmount() {
    const { $app } = this.props;
    $app.toggleKioskMode({ forceValue: false });
  }

  get hotkeys() {
    const { $app, onRevertBoard } = this.props;

    return [
      {
        global: true,
        group: HotkeyGroup.DashboardSavedView,
        combo: 'cmd + s',
        label: 'Save',
        preventDefault: true,
        stopPropagation: true,
        onKeyDown: this.handleSaveBoard
      },
      {
        global: true,
        group: HotkeyGroup.DashboardSavedView,
        combo: 'cmd + z',
        label: 'Discard Changes',
        preventDefault: true,
        stopPropagation: true,
        onKeyDown: onRevertBoard
      },
      {
        global: true,
        group: HotkeyGroup.DashboardSavedView,
        combo: 'f11',
        label: 'Toggle Kiosk Mode',
        preventDefault: true,
        stopPropagation: true,
        onKeyDown: $app.toggleKioskMode
      }
    ];
  }

  handleToggleSettingsDrawer = () => {
    const { isSettingsOpen } = this.state;
    this.setState({ isSettingsOpen: !isSettingsOpen, isFiltersOpen: false });
  };

  handleToggleFiltersDrawer = () => {
    const { isFiltersOpen } = this.state;
    this.setState({ isFiltersOpen: !isFiltersOpen, isSettingsOpen: false });
  };

  handlePresetFiltersChange = (presetFilters) => {
    const { board } = this.props;
    board.set({
      config: {
        ...board.get('config'),
        presetFilters
      }
    });
  };

  handleSaveTitle = ({ title }) => {
    const { onSaveBoard } = this.props;
    return onSaveBoard({ title }, { draft: true });
  };

  handleBreadcrumbTitleChange = (event) => {
    const { board } = this.props;
    board.safeSet({ title: event.target.value });
  };

  render() {
    const { $app, board, onSaveBoard, onAddPanel, onEditTextPanel, scrollRef, collapsed } = this.props;
    const { isSettingsOpen, isFiltersOpen } = this.state;

    if (!board) {
      return (
        <NotFound title={`${BOARD_LABEL} Not Found`} text={`Unable to find this ${BOARD_LABEL}.`} hideSubnav={false} />
      );
    }

    const { canEdit } = board;
    const panels = board.panelCollection.topLevelPanels;
    const { hasUdePanel } = board.panelCollection;

    let drawerContents = null;

    if (isSettingsOpen) {
      drawerContents = <BoardSettings board={board} onClose={this.handleToggleSettingsDrawer} />;
    } else if (isFiltersOpen) {
      drawerContents = <BoardFiltersDrawer board={board} onClose={this.handleToggleFiltersDrawer} />;
    }

    const titleElement = (
      <Flex alignItems="center" gap={1} style={{ minWidth: 0 }}>
        {canEdit ? (
          <BreadcrumbTitleInput
            value={board.get('title') || ''}
            onChange={this.handleBreadcrumbTitleChange}
            aria-label="Dashboard title"
            style={{ width: `min(300px, calc(${(board.get('title') || '').length}ch + 100px))` }}
          />
        ) : (
          <BoardTitle board={board} canEdit={false} onSave={this.handleSaveTitle} />
        )}
        <LabelList labels={board.labels} shareLevel={board.shareLevel} maxLabelsToShow={1} />
      </Flex>
    );

    const collapsibleHeader = <BoardCollapsibleHeader board={board} canEdit={canEdit} />;

    const stickyToolbar = hasUdePanel ? (
      <UdeToolBar
        board={board}
        onTimeChange={board.setTimeRange}
        onTimezoneChange={board.setTimezone}
        onLiveUpdateChange={board.setLiveUpdate}
        onOpenFilters={this.handleToggleFiltersDrawer}
        sticky
        collapsed={collapsed}
      />
    ) : null;

    return (
      <Page
        title={board.get('title')}
        titleElement={titleElement}
        px={0}
        py={0}
        overflow="hidden"
        hideSubnav={$app.isKioskMode}
        subnavTools={
          <BoardSubnavTools
            board={board}
            onAddPanel={onAddPanel}
            showAddButton={panels.length > 0}
            onSave={onSaveBoard}
            onShowSettings={this.handleToggleSettingsDrawer}
            onPresetFiltersChange={this.handlePresetFiltersChange}
          />
        }
        drawerProps={{ showDockedInLargeFullScreen: false }}
        drawerContents={drawerContents}
        drawerIsOpen={isSettingsOpen || isFiltersOpen}
        drawerOnClose={isFiltersOpen ? this.handleToggleFiltersDrawer : this.handleToggleSettingsDrawer}
      >
        <UnsavedChangesPrompt board={board} />
        <HotkeysTarget2 hotkeys={this.hotkeys}>
          <BoardContentColumn overflow="hidden" flex={1} height={`calc(100vh - ${NAV_HEIGHT}px)`}>
            <BoardKioskHeader board={board} />
            <BoardGridView
              board={board}
              canEdit={canEdit}
              scrollRef={scrollRef}
              headerSlot={collapsibleHeader}
              stickyToolbarSlot={stickyToolbar}
              onAddPanel={onAddPanel}
              onEditTextPanel={onEditTextPanel}
            />
          </BoardContentColumn>
        </HotkeysTarget2>
      </Page>
    );
  }
}

function BoardViewWithScroll(props) {
  const { collapsed, scrollRef } = useBoardScrollBehavior();
  return <BoardView {...props} collapsed={collapsed} scrollRef={scrollRef} />;
}

export default BoardViewWithScroll;
